import React, { useState, useEffect } from 'react';
import { artistService } from '../../../services/musicService';

const inputStyle = { background: '#0d0d0d', border: '1px solid #2a2a2a', color: 'white' };

function AlbumForm({ initialData, onSubmit, onCancel, title = 'Add Album' }) {
  const [formData, setFormData] = useState({ title: '', artist_id: '', release_year: '', cover_image_url: '' });
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [focusedField, setFocusedField] = useState(null);

  useEffect(() => {
    fetchArtists();
    if (initialData) {
      setFormData({
        title: initialData.title || '',
        artist_id: initialData.artist_id || '',
        release_year: initialData.release_year || '',
        cover_image_url: initialData.cover_image_url || ''
      });
    }
  }, [initialData]);

  const fetchArtists = async () => {
    try {
      setLoading(true);
      const response = await artistService.getAllArtists();
      setArtists(response.data?.data || []);
    } catch (error) {
      console.error('Failed to fetch artists:', error);
    } finally {
      setLoading(false);
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.artist_id) newErrors.artist_id = 'Artist is required';
    if (formData.release_year) {
      const year = parseInt(formData.release_year);
      const currentYear = new Date().getFullYear();
      if (year < 1900 || year > currentYear) newErrors.release_year = `Year must be between 1900 and ${currentYear}`;
    }
    if (formData.cover_image_url && !formData.cover_image_url.startsWith('http')) newErrors.cover_image_url = 'Please enter a valid URL';
    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) { setErrors(validationErrors); return; }
    onSubmit({ ...formData, release_year: formData.release_year || null, cover_image_url: formData.cover_image_url || null });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const getFieldStyle = (fieldName) => ({
    ...inputStyle,
    ...(focusedField === fieldName ? { borderColor: 'rgba(220,38,38,0.6)', boxShadow: '0 0 0 2px rgba(220,38,38,0.1)', outline: 'none' } : {}),
    ...(errors[fieldName] ? { borderColor: '#dc2626' } : {}),
  });

  const currentYear = new Date().getFullYear();

  return (
    <div className="rounded-xl overflow-hidden" style={{ background: '#111', border: '1px solid #1f1f1f' }}>
      <div className="px-6 py-4 flex items-center justify-between" style={{ borderBottom: '1px solid #1a1a1a', background: '#0d0d0d' }}>
        <div className="flex items-center gap-3">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: '#dc2626' }}>
            <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
          </div>
          <h2 className="text-base font-black text-white uppercase tracking-wide">{title}</h2>
        </div>
        <button onClick={onCancel} style={{ color: '#6b7280' }} className="hover:text-white transition-colors">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <form onSubmit={handleSubmit} className="p-6">
        <div className="space-y-5">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: '#9ca3af' }}>Title *</label>
            <input
              type="text" name="title" value={formData.title} onChange={handleChange}
              className="w-full px-4 py-3 rounded-lg text-sm text-white placeholder-gray-600 transition-all"
              style={getFieldStyle('title')}
              onFocus={() => setFocusedField('title')} onBlur={() => setFocusedField(null)}
              placeholder="Enter album title"
            />
            {errors.title && <p className="mt-1 text-xs" style={{ color: '#f87171' }}>{errors.title}</p>}
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: '#9ca3af' }}>Artist *</label>
            <select
              name="artist_id" value={formData.artist_id} onChange={handleChange} disabled={loading}
              className="w-full px-4 py-3 rounded-lg text-sm text-white transition-all"
              style={getFieldStyle('artist_id')}
              onFocus={() => setFocusedField('artist_id')} onBlur={() => setFocusedField(null)}
            >
              <option value="" style={{ background: '#111' }}>Select an artist</option>
              {artists.map(artist => (
                <option key={artist.artist_id || artist.id} value={artist.artist_id || artist.id} style={{ background: '#111' }}>
                  {artist.name}
                </option>
              ))}
            </select>
            {errors.artist_id && <p className="mt-1 text-xs" style={{ color: '#f87171' }}>{errors.artist_id}</p>}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: '#9ca3af' }}>Release Year</label>
              <select
                name="release_year" value={formData.release_year} onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg text-sm text-white transition-all"
                style={getFieldStyle('release_year')}
                onFocus={() => setFocusedField('release_year')} onBlur={() => setFocusedField(null)}
              >
                <option value="" style={{ background: '#111' }}>Select year</option>
                {Array.from({ length: currentYear - 1899 }, (_, i) => currentYear - i).map(year => (
                  <option key={year} value={year} style={{ background: '#111' }}>{year}</option>
                ))}
              </select>
              {errors.release_year && <p className="mt-1 text-xs" style={{ color: '#f87171' }}>{errors.release_year}</p>}
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: '#9ca3af' }}>Cover Image URL</label>
              <input
                type="url" name="cover_image_url" value={formData.cover_image_url} onChange={handleChange}
                className="w-full px-4 py-3 rounded-lg text-sm text-white placeholder-gray-600 transition-all"
                style={getFieldStyle('cover_image_url')}
                onFocus={() => setFocusedField('cover_image_url')} onBlur={() => setFocusedField(null)}
                placeholder="https://example.com/image.jpg"
              />
              {errors.cover_image_url && <p className="mt-1 text-xs" style={{ color: '#f87171' }}>{errors.cover_image_url}</p>}
            </div>
          </div>

          {formData.cover_image_url && (
            <div className="p-3 rounded-lg" style={{ background: '#0d0d0d', border: '1px solid #1a1a1a' }}>
              <p className="text-xs mb-2" style={{ color: '#6b7280' }}>Preview</p>
              <div className="w-24 h-24 rounded-xl overflow-hidden" style={{ background: '#1a1a1a' }}>
                <img src={formData.cover_image_url} alt="Cover preview" className="w-full h-full object-cover"
                  onError={(e) => { e.target.style.display = 'none'; }} />
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-3 mt-6">
          <button type="submit" className="flex-1 py-3 rounded-lg font-bold text-white text-sm transition-all hover:opacity-90"
            style={{ background: '#dc2626' }}>
            {initialData ? 'Update Album' : 'Create Album'}
          </button>
          <button type="button" onClick={onCancel} className="flex-1 py-3 rounded-lg text-sm font-medium transition-all"
            style={{ background: '#0d0d0d', color: '#9ca3af', border: '1px solid #2a2a2a' }}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

export default AlbumForm;